import { useEffect, useState } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Link, useOutletContext } from 'react-router-dom';
import { AlertTriangle, History, KeyRound, RefreshCw, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { boPath } from '@/utils/getBasePath';
import ApprovalCodesCard from '@/components/approval/ApprovalCodesCard';
import { ROLE_WORDS, approvalHistory, errorWords, getApprovalCodes, saveSwitches } from '@/lib/approvalCodes';

// Approval codes (owner, Oct 29 2026): "In the owner portal, put a LIST of actions that
// need an approval code; the owner turns each on with a check mark; ALL OFF by default."
// Off: each role does what it may (no code). On: everyone but an owner / manager is asked
// for an owner's / manager's own approval code on the POS. Every door checks it on the
// server (security/approval_gate); this page only turns the switches.

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '');

export default function ApprovalCodesPage() {
  const context = useOutletContext() || {};
  const token = context.token || localStorage.getItem('backoffice_token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [events, setEvents] = useState(null);

  const load = useLatestCallback(async () => {
    try {
      setData(await getApprovalCodes(token));
    } catch (e) {
      toast.error(errorWords(e, 'Could not load approval codes'));
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => { if (token) load(); }, [token, load]);

  const toggle = async (action) => {
    setSaving(action.key);
    try {
      const view = await saveSwitches(token, { [action.key]: !action.enabled });
      setData(view);
      toast.success(`${action.label}: ${action.enabled ? 'no code needed' : 'approval code needed'}`);
      if (events !== null) setEvents(await approvalHistory(token));
    } catch (e) {
      toast.error(errorWords(e, 'Could not change this switch'));
    } finally {
      setSaving(null);
    }
  };

  const showHistory = async () => {
    try { setEvents(await approvalHistory(token)); } catch (e) { toast.error(errorWords(e, 'Could not load the history')); }
  };

  const actions = data?.actions || [];
  const owner = !!data?.me?.can_change_switches;
  const onCount = actions.filter((a) => a.enabled).length;
  const nameOf = (ref) => (data?.approvers || []).find((a) => a.person === ref)?.name || 'Someone';
  const live = actions.filter((a) => a.applies);
  const ownerOnly = actions.filter((a) => !a.applies);

  const row = (a) => (
    <li key={a.key} className="p-3 sm:p-4 flex items-start gap-3" data-testid={`approval-switch-${a.key}`}>
      <input type="checkbox" className="mt-1 h-5 w-5 accent-blue-600 cursor-pointer disabled:cursor-not-allowed"
        checked={a.enabled} disabled={!owner || !a.applies || saving === a.key} onChange={() => toggle(a)}
        aria-label={a.label} data-testid={`approval-check-${a.key}`} id={`approval-${a.key}`} />
      <label htmlFor={`approval-${a.key}`} className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-slate-900">{a.label}</span>
          {a.enabled && <Badge className="bg-amber-100 text-amber-900">Code needed</Badge>}
        </span>
        <span className="block text-sm text-slate-600 mt-0.5">Example: {a.example}</span>
        <span className="block text-xs text-slate-500 mt-1">
          {a.applies
            ? <>Asks: {a.asked_roles.map((r) => ROLE_WORDS[r] || r).join(', ')}. Owners and managers are never asked.</>
            : <>Only owners and managers can do this today, so nobody would be asked for a code.</>}
        </span>
      </label>
    </li>
  );

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="approval-codes-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900 md:text-2xl flex items-center gap-2">
            <KeyRound className="w-6 h-6 text-blue-600" /> Approval codes
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Tick an action to make a cashier or server get an owner's or manager's approval code before doing it.
            Everything is off until you turn it on.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild className="min-h-[44px]">
            <Link to={boPath('pos-login-access')}><ShieldCheck className="w-4 h-4 mr-2" />POS Login Access</Link>
          </Button>
          <Button variant="outline" onClick={load} className="min-h-[44px]" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {data?.needs_code_setup && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3" role="alert" data-testid="approval-setup-prompt">
          <p className="text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" />
            <span>An action is switched on, but no owner or manager has an approval code yet, so nobody can approve it on the POS.
              Set your approval code now.</span>
          </p>
          <ApprovalCodesCard token={token} data={data} onChanged={setData} compact />
        </div>
      )}

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Actions that need an approval code ({onCount} on)</CardTitle>
          <CardDescription>
            {owner ? 'Changes save as you tick. With a switch off, each role does only what it is allowed to (no code).'
              : 'Only the owner turns these on or off.'}
            {data?.updated_at ? ` Last changed ${when(data.updated_at)} by ${nameOf(data.updated_by)}.` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-8 text-center"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : (
            <>
              <ul className="divide-y divide-slate-100">{live.map(row)}</ul>
              {ownerOnly.length > 0 && (
                <>
                  <p className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Owner and manager only today</p>
                  <ul className="divide-y divide-slate-100">{ownerOnly.map(row)}</ul>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {data && !data.needs_code_setup && <ApprovalCodesCard token={token} data={data} onChanged={setData} />}

      <Card className="border-slate-200">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" />Changes</CardTitle>
          {events === null && <Button variant="outline" onClick={showHistory} className="min-h-[44px]">Show</Button>}
        </CardHeader>
        {events !== null && (
          <CardContent>
            {events.length === 0 ? <p className="text-sm text-slate-600">No changes yet.</p> : (
              <ul className="text-sm text-slate-700 space-y-1" data-testid="approval-history">
                {events.map((ev, i) => (
                  <li key={i}>
                    {when(ev.at)} - {nameOf(ev.by)}{' '}
                    {ev.action === 'approval_setting.on' ? `turned on "${ev.label}"`
                      : ev.action === 'approval_setting.off' ? `turned off "${ev.label}"`
                        : ev.action === 'approval_code.removed' ? `removed ${nameOf(ev.what)}'s approval code`
                          : `set ${nameOf(ev.what)}'s approval code`}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
